import React, { useEffect, useRef, useState } from "react";
import { Props, State } from "./interfaces";
import { useTheme } from "@mui/material/styles";
import { Dialog } from "@mui/material";
import DialogTitle from "../../../../../containers/DialogTitle/DialogTitle";
import { DialogContent, NoSkills, SkillsBlock, SkillsContainer, SkillsInfoBlock } from "./styles";
import DialogActions from "../../../../../containers/DialogActions/DialogActions";
import CustomTable from "../../../../../reusable-components/Table/CustomTable/CustomTable";
import { SuggestedCandidatesApiService } from "../../../../../services/recruiting/positions/suggested-candidates/suggested-candidates-api.service";
import { FT_SCT } from "../../../../../utils/constants/filter-tables-constants";
import { GridColumns } from "@mui/x-data-grid";
import { CandidateDTO } from "../../../../../models/recruiting/candidate/candidate-dto";
import { SKILLS_CATEGORIES } from "../../../../../utils/constants/constants";
import { CandidateSkillRef } from "../../../../../models/recruiting/candidate/skills/candidate-skill-ref";
import { PositionPropsFilter } from "../../../../../models/recruiting/positions/position-props-filter";
import QuickfiltersContainer, {
  FiltersContainer,
} from "../../../../../containers/Quickfilters/QuickfiltersContainer/QuickfiltersContainer";
import { useAvailabilityStatusesQuickFilter } from "../../../../../containers/Quickfilters/AvailabilityQuickfilter/useAvailabilityStatusesQuickFilter";
import { AvailabilityStatusesQuickfilter } from "../../../../../containers/Quickfilters/AvailabilityQuickfilter/AvailabilityStatusesQuickfilter";
import { CountriesQuickfilter } from "../../../../../containers/Quickfilters/CountriesQuickfilter/CountriesQuickfilter";
import { RequiredSkillsQuickfilter } from "../../../../../containers/Quickfilters/RequiredSkillsQuickfilter/RequiredSkillsQuickfilter";
import { useTranslation } from "react-i18next";

const SuggestedCandidates = ({
  positionId,
  currPosition,
  selectedRowsChecked,
  isOpen,
  closeDialog,
  saveCandidates,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "components.suggested_candidates";
  const translationFields = `${tRP}.fields`;
  const theme = useTheme();
  const checkedCandidates = useRef([]);

  const requiredCountriesIds = currPosition?.countries?.map((country) => country.id) ?? [];

  const requiredSkillsIds = currPosition?.requiredSkills?.map((currSkill) => currSkill?.skill?.id as string) ?? [];

  const [countryIds, setCountryIds] = useState<string[]>(requiredCountriesIds);
  const [skillIds, setSkillsIds] = useState<string[]>(requiredSkillsIds);

  const unmounted = useRef(false);

  const { availabilityStatusesQuickFilter, setAvailabilityStatusesQuickFilter } = useAvailabilityStatusesQuickFilter();

  const propsFilter = {
    positionId: positionId,
    availabilityId: availabilityStatusesQuickFilter.availabilityStatuses.availabilityStatusesId,
    countryIds: countryIds,
    skillIds: skillIds,
  };

  const [state, setState] = useState<State>({
    openDialog: isOpen,
    loading: false,
    error: "",
  });

  const handleCloseDialog = () => {
    setState((state) => ({
      ...state,
      error: "",
      openDialog: false,
    }));
    unmounted.current = true;
    closeDialog();
  };

  const languageSkills = currPosition.requiredSkills?.filter(
    (currSkill) => currSkill?.skill?.category?.id === SKILLS_CATEGORIES.language.id
  );

  const personalSkills = currPosition.requiredSkills?.filter(
    (currSkill) => currSkill?.skill?.category?.id === SKILLS_CATEGORIES.personal.id
  );

  const technicalSkills = currPosition.requiredSkills?.filter(
    (currSkill) => currSkill?.skill?.category?.id === SKILLS_CATEGORIES.technical.id
  );

  const handleSave = () => {
    if (selectedRowsChecked) {
      selectedRowsChecked.current = checkedCandidates.current;
    }
    handleCloseDialog();
    saveCandidates();
  };

  useEffect(() => {
    return () => {
      setState({
        openDialog: false,
        loading: false,
        error: "",
      });
    };
  }, []);

  const columns: GridColumns = [
    {
      field: "name",
      valueGetter: ({ row }) => row?.name,
      headerName: t(`${translationFields}.name`),
      headerAlign: "center",
      align: "left",
      flex: 1,
      minWidth: 100,
      maxWidth: 200,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: "country",
      renderCell: ({ row }) => <span title={row?.country?.name}>{row?.country?.code}</span>,
      headerName: t(`${translationFields}.country`),
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 120,
      maxWidth: 120,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: "availability",
      renderCell: ({ row }) => <span title={row?.availability?.name}>{row?.availability?.code}</span>,
      headerName: t(`${translationFields}.availability`),
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 120,
      maxWidth: 120,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: "technical",
      headerName: t(`${translationFields}.technical_skills`),
      renderCell: ({ row }) => (
        <SkillsContainer>
          {!!technicalSkills &&
            technicalSkills.map((requiredSkill, index) => {
              const candidateSkill = row.skills.find(
                (currSkill: CandidateSkillRef) => currSkill.skill?.id === requiredSkill?.skill?.id
              );

              const isAppropriateLevel =
                requiredSkill?.skillLevel?.numericLevel && candidateSkill?.skillLevel?.numericLevel
                  ? candidateSkill?.skillLevel?.numericLevel >= requiredSkill?.skillLevel?.numericLevel
                    ? true
                    : false
                  : false;

              return (
                <SkillsBlock key={requiredSkill?.id}>
                  <span
                    className={"skillName"}
                    title={requiredSkill.skill?.name}
                  >
                    {requiredSkill.skill?.name}:
                  </span>

                  {candidateSkill ? (
                    <SkillsInfoBlock>
                      <span
                        style={{
                          whiteSpace: "nowrap",
                          maxWidth: "75px",
                          color: isAppropriateLevel ? theme.palette.success.main : "inherit",
                        }}
                      >
                        {candidateSkill?.skillLevel.name}&nbsp;
                      </span>
                      <span className={"yearsOfExperience"}>{`- ${candidateSkill?.yearsOfExperience} y`}</span>
                    </SkillsInfoBlock>
                  ) : (
                    <SkillsInfoBlock>
                      <NoSkills>N/A</NoSkills>
                    </SkillsInfoBlock>
                  )}
                </SkillsBlock>
              );
            })}

          {row?.skills
            ?.filter(
              (currSkill: CandidateSkillRef) => currSkill?.skill?.category?.id === SKILLS_CATEGORIES.technical.id
            )
            .filter((candidateSkill: CandidateSkillRef) => {
              return !technicalSkills?.find((currSkill) => currSkill?.skill?.id === candidateSkill?.skill?.id);
            })
            .map((otherSkill: CandidateSkillRef, index: number) => {
              return (
                <SkillsBlock key={otherSkill?.id}>
                  <span
                    className={"otherSkillName"}
                    title={otherSkill.skill?.name}
                  >
                    {otherSkill.skill?.name}:
                  </span>

                  <SkillsInfoBlock>
                    <span className={"skillLevel"}>{otherSkill?.skillLevel?.name}&nbsp;</span>
                    <span className={"yearsOfExperience"}>{`- ${otherSkill?.yearsOfExperience} y`}</span>
                  </SkillsInfoBlock>
                </SkillsBlock>
              );
            })}
        </SkillsContainer>
      ),

      align: "left",
      headerAlign: "center",
      flex: 1,
      minWidth: 250,
      cellClassName: "flexStart",
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: "language",
      headerName: t(`${translationFields}.language_skills`),
      renderCell: ({ row }) => (
        <SkillsContainer>
          {!!languageSkills &&
            languageSkills.map((requiredSkill, index) => {
              const requiredCandidateSkill = row.skills.find(
                (currSkill: CandidateSkillRef) => currSkill.skill?.id === requiredSkill?.skill?.id
              );

              const isAppropriateLevel =
                requiredSkill?.skillLevel?.numericLevel && requiredCandidateSkill?.skillLevel?.numericLevel
                  ? requiredCandidateSkill?.skillLevel?.numericLevel >= requiredSkill?.skillLevel?.numericLevel
                    ? true
                    : false
                  : false;

              return (
                <SkillsBlock key={requiredSkill?.id}>
                  <span
                    className={"skillName"}
                    title={requiredSkill.skill?.name}
                  >
                    {requiredSkill.skill?.name}:
                  </span>

                  {requiredCandidateSkill ? (
                    <SkillsInfoBlock>
                      <span
                        style={{
                          whiteSpace: "nowrap",
                          maxWidth: "75px",
                          color: isAppropriateLevel ? theme.palette.success.main : "inherit",
                        }}
                      >
                        {requiredCandidateSkill?.skillLevel.name}&nbsp;
                      </span>
                      <span className={"yearsOfExperience"}>{`- ${requiredCandidateSkill?.yearsOfExperience} y`}</span>
                    </SkillsInfoBlock>
                  ) : (
                    <SkillsInfoBlock>
                      <NoSkills>N/A</NoSkills>
                    </SkillsInfoBlock>
                  )}
                </SkillsBlock>
              );
            })}

          {row?.skills
            ?.filter((currSkill: CandidateSkillRef) => currSkill?.skill?.category?.id === SKILLS_CATEGORIES.language.id)
            .filter((candidateSkill: CandidateSkillRef) => {
              return !languageSkills?.find((currSkill) => currSkill?.skill?.id === candidateSkill?.skill?.id);
            })
            .map((otherSkill: CandidateSkillRef, index: number) => {
              return (
                <SkillsBlock key={otherSkill?.id}>
                  <span
                    className={"otherSkillName"}
                    title={otherSkill.skill?.name}
                  >
                    {otherSkill.skill?.name}:
                  </span>

                  <SkillsInfoBlock>
                    <span className={"skillLevel"}>{otherSkill?.skillLevel?.name}&nbsp;</span>
                    <span className={"yearsOfExperience"}>{`- ${otherSkill?.yearsOfExperience} y`}</span>
                  </SkillsInfoBlock>
                </SkillsBlock>
              );
            })}
        </SkillsContainer>
      ),

      align: "left",
      headerAlign: "center",
      flex: 1,
      minWidth: 250,
      cellClassName: "flexStart",
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: "personal",
      headerName: t(`${translationFields}.personal_skills`),
      renderCell: ({ row }) => (
        <SkillsContainer>
          {!!personalSkills &&
            personalSkills.map((requiredSkill, index) => {
              const candidateSkill = row.skills.find(
                (currSkill: CandidateSkillRef) => currSkill.skill?.id === requiredSkill?.skill?.id
              );

              const isAppropriateLevel =
                requiredSkill?.skillLevel?.numericLevel && candidateSkill?.skillLevel?.numericLevel
                  ? candidateSkill?.skillLevel?.numericLevel >= requiredSkill?.skillLevel?.numericLevel
                    ? true
                    : false
                  : false;

              return (
                <SkillsBlock key={requiredSkill?.id}>
                  <span
                    className={"skillName"}
                    title={requiredSkill.skill?.name}
                  >
                    {requiredSkill.skill?.name}:
                  </span>

                  {candidateSkill ? (
                    <SkillsInfoBlock>
                      <span
                        style={{
                          whiteSpace: "nowrap",
                          maxWidth: "75px",
                          color: isAppropriateLevel ? theme.palette.success.main : "inherit",
                        }}
                      >
                        {candidateSkill?.skillLevel.name}&nbsp;
                      </span>
                      <span className={"yearsOfExperience"}>{`- ${candidateSkill?.yearsOfExperience} y`}</span>
                    </SkillsInfoBlock>
                  ) : (
                    <SkillsInfoBlock>
                      <NoSkills>N/A</NoSkills>
                    </SkillsInfoBlock>
                  )}
                </SkillsBlock>
              );
            })}

          {row?.skills
            ?.filter((currSkill: CandidateSkillRef) => currSkill?.skill?.category?.id === SKILLS_CATEGORIES.personal.id)
            .filter((candidateSkill: CandidateSkillRef) => {
              return !personalSkills?.find((currSkill) => currSkill?.skill?.id === candidateSkill?.skill?.id);
            })
            .map((otherSkill: CandidateSkillRef, index: number) => {
              return (
                <SkillsBlock key={otherSkill?.id}>
                  <span
                    className={"otherSkillName"}
                    title={otherSkill.skill?.name}
                  >
                    {otherSkill.skill?.name}:
                  </span>

                  <SkillsInfoBlock>
                    <span className={"skillLevel"}>{otherSkill?.skillLevel?.name}&nbsp;</span>
                    <span className={"yearsOfExperience"}>{`- ${otherSkill?.yearsOfExperience} y`}</span>
                  </SkillsInfoBlock>
                </SkillsBlock>
              );
            })}
        </SkillsContainer>
      ),

      align: "left",
      headerAlign: "center",
      flex: 1,
      minWidth: 250,
      cellClassName: "flexStart",
      sortable: false,
      disableColumnMenu: true,
    },
  ];

  const defaultItem: Partial<CandidateDTO> = {
    id: "",
    name: "",
  };

  return (
    <>
      <Dialog
        open={state.openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth={"lg"}
      >
        <DialogTitle
          title={""}
          handleClose={handleCloseDialog}
        />
        <DialogContent>
          <CustomTable
            translationRootPath={tRP}
            columns={columns}
            serviceApi={new SuggestedCandidatesApiService()}
            propsFilter={propsFilter as PositionPropsFilter}
            defaultItem={defaultItem}
            filter={FT_SCT}
            readOnly
            autoHeight={true}
            checkedCandidates={checkedCandidates}
            unmounted={unmounted}
            quickFilters={
              <QuickfiltersContainer>
                <FiltersContainer>
                  <AvailabilityStatusesQuickfilter
                    quickFilters={availabilityStatusesQuickFilter}
                    handleChangeQuickFilters={setAvailabilityStatusesQuickFilter}
                  />
                  <CountriesQuickfilter
                    countries={currPosition?.countries}
                    setCountryIds={setCountryIds}
                  />
                  <RequiredSkillsQuickfilter
                    requiredSkills={currPosition?.requiredSkills}
                    setSkillsIds={setSkillsIds}
                  />
                </FiltersContainer>
              </QuickfiltersContainer>
            }
          />
        </DialogContent>
        <DialogActions
          handleCancel={handleCloseDialog}
          handleSave={handleSave}
          loading={state.loading}
          error={state.error}
        />
      </Dialog>
    </>
  );
};

export default SuggestedCandidates;
